import { createAsyncThunk } from '@reduxjs/toolkit';
import { createGraphQLClient } from '@api';
import type { RootState } from '@store';
import { SERVER_ERROR } from '@config/constants';
import {
  GET_CART_DISCOUNTS_QUERY, GetCartDiscountsResponse,
  GET_CART_QUERY, GetCartResponse,
  UPDATE_PRODUCT_QUANTITY_MUTATION, UpdateProductQuantityResponse,
} from '@requests/cart';
import { cartSelector } from '@store/slices/cart/selectors';

export interface UpdateProductQtyParams {
  uid: string;
  qty: number;
  controller?: AbortController | null;
}

type UpdateProductQuantityResult = GetCartResponse & GetCartDiscountsResponse;

export const updateProductQty = createAsyncThunk<UpdateProductQuantityResult, UpdateProductQtyParams>(
  'cart/updateProductQty',
  async (params, {
    rejectWithValue, getState,
  }) => {
    try {
      const state = getState() as RootState;
      const { cartId, products } = cartSelector(state);
      const product = products.find(({ uid }) => uid === params.uid);

      const uid = cartId as string;

      const client = createGraphQLClient(params.controller?.signal);
      await client.request<UpdateProductQuantityResponse>(UPDATE_PRODUCT_QUANTITY_MUTATION, {
        orderUid: uid,
        orderItemUid: product?.uid,
        qty: params.qty,
      });

      const [cartData, cartDiscounts] = await Promise.all([
        client.request<GetCartResponse>(GET_CART_QUERY, { uid }),
        client.request<GetCartDiscountsResponse>(GET_CART_DISCOUNTS_QUERY, { uid }),
      ]);

      return { ...cartData, ...cartDiscounts };
    } catch (error) {
      return rejectWithValue(SERVER_ERROR);
    }
  },
);
